import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getCountFromServer, onSnapshot, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase/config';
import { approvalIsExpired } from './utils/agentApprovals';

// Login, the route guard, and the layout shells stay static so the login
// screen paints instantly. Every page is a route-level lazy chunk, grouped
// by user type so each role only downloads its own world.
import Login, { roleDestination } from './pages/Login';
import Landing           from './pages/Landing';
import PrivateRoute      from './components/PrivateRoute';
import { RoofBeamWordmark } from './components/RoofBeamLogo';
import { AppAlertHost } from './components/AppAlert';
import UpdateBanner from './components/UpdateBanner';
import useAppResumeAuthCheck from './hooks/useAppResumeAuthCheck';
import useNativeBackButton from './hooks/useNativeBackButton';
import usePushNotifications from './hooks/usePushNotifications';
import AddToHomeScreenPrompt from './components/AddToHomeScreenPrompt';
// ── FIELD MODE ──────────────────────────────────────────────────────────────
// The chrome the app wears on a phone. See src/utils/layoutMode.js — the rule
// that makes it safe is that MODE CHANGES THE CHROME ONLY, NEVER THE URL.
import useLayoutMode from './hooks/useLayoutMode';
import { FIELD, heavyRoute } from './utils/layoutMode';
import FieldNav from './components/FieldNav';
import BestOnLargerScreen from './components/BestOnLargerScreen';
import './App.css';

// ── Build stamp ──────────────────────────────────────────────────────────────
// REACT_APP_BUILD_SHA is baked in at build time by the npm build script
// (git rev-parse --short HEAD). Rendering it in the shell means the running
// bundle identifies itself on screen — no DevTools archaeology to answer
// "am I actually on the deploy that was reported?". 'dev' in local dev server.
export const BUILD_SHA = process.env.REACT_APP_BUILD_SHA || 'dev';
console.log(`RoofBeam build ${BUILD_SHA}`);

// ONE definition, mounted in both shells — never a second copy to drift.
function BuildStamp({ style }) {
  return (
    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.3px', whiteSpace: 'nowrap', ...style }}>
      build {BUILD_SHA}
    </div>
  );
}

// Total unread across the signed-in user's message threads → nav badge.
// Inline (not imported from the lazy Messages chunk) so the nav paints eagerly.
function useTotalUnread() {
  const [total, setTotal] = useState(0);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;
    return onSnapshot(
      query(collection(db, 'threads'), where('participants', 'array-contains', uid)),
      snap => { let n = 0; snap.docs.forEach(d => { n += Number((d.data().unreadBy || {})[uid] || 0); }); setTotal(n); },
      () => setTotal(0),
    );
  }, []);
  return total;
}

// Count of pending storm-dispatcher approvals (chief_bot-filed) → nav badge,
// so the owner sees when the approval email he got has something still
// waiting. Live (onSnapshot), not a one-time count — Sidebar only mounts
// once the owner is authenticated (every AppLayout route is owner-only per
// routeRoles.js), so no auth guard is needed here the way useTotalUnread
// needs one for its uid-keyed query.
// ⚠️ AN EXPIRED APPROVAL IS NOT PENDING. Two chief_bot requests from 6 and 12
// September still carry status:'pending' in the database — nothing moves a
// stale one — so this badge said "2" and the /approvals page showed both as
// "Expired" with live-looking Approve & Run buttons. The 48-hour rule is
// already written down (agentApprovals.approvalIsExpired); the count just was
// not using it. The query still has to be on status, because that is what is
// indexed; the expiry is applied to the result.
function usePendingApprovalsCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'approvals'), where('status', '==', 'pending')),
      snap => setCount(snap.docs.filter(d => !approvalIsExpired({ id: d.id, ...d.data() })).length),
      () => setCount(0),
    );
  }, []);
  return count;
}

// Owner pages
const Dashboard         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Dashboard'));
const Leads             = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Leads'));
const WebsiteLeads      = lazy(() => import(/* webpackChunkName: "owner" */ './pages/WebsiteLeads'));
const Prospects         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Prospects'));
const Proposals         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Proposals'));
const NotFound          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/NotFound'));
const Permits           = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Permits'));
const RoofbeamOS        = lazy(() => import(/* webpackChunkName: "owner" */ './pages/RoofbeamOS'));
const Jobs              = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Jobs'));
const JobDetail         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/JobDetail'));
const Takeoff           = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Takeoff'));
const MetalJobs           = lazy(() => import(/* webpackChunkName: "owner" */ './pages/MetalJobs'));
const Estimator         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Estimator'));
const CatalogPage       = lazy(() => import(/* webpackChunkName: "owner" */ './pages/CatalogPage'));
const Contacts          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Contacts'));
const ContactDetail     = lazy(() => import(/* webpackChunkName: "owner" */ './pages/ContactDetail'));
// Both /contacts and /contacts/:id render this: it decides one pane or two
// from the window, and keeps the selection in the URL so a fold cannot lose it.
const ContactsSplit     = lazy(() => import(/* webpackChunkName: "owner" */ './pages/ContactsSplit'));
const Properties        = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Properties'));
const PropertyDetail    = lazy(() => import(/* webpackChunkName: "owner" */ './pages/PropertyDetail'));
const Documents         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Documents'));
const DocumentViewer    = lazy(() => import(/* webpackChunkName: "owner" */ './pages/DocumentViewer'));
const ProfitTracker     = lazy(() => import(/* webpackChunkName: "owner" */ './pages/ProfitTracker'));
const AIAgents          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/AIAgents'));
const SolarBatch        = lazy(() => import(/* webpackChunkName: "owner" */ './pages/SolarBatch'));
const Contractors       = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Contractors'));
const Commercial        = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Commercial'));
const NeighborhoodIntel = lazy(() => import(/* webpackChunkName: "owner" */ './pages/NeighborhoodIntel'));
const Settings          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Settings'));
const Calendar          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Calendar'));
const LaborLog          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/LaborLog'));
const Todos             = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Todos'));
const Messages          = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Messages'));
const Approvals         = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Approvals'));
const Claims             = lazy(() => import(/* webpackChunkName: "owner" */ './pages/Claims'));

// Map-heavy pages (everything importing leaflet)
const StormTracker      = lazy(() => import(/* webpackChunkName: "maps" */ './pages/StormTracker'));
const MapView           = lazy(() => import(/* webpackChunkName: "maps" */ './pages/MapView'));
const CanvassPage       = lazy(() => import(/* webpackChunkName: "maps" */ './pages/CanvassPage'));
const Canvassing        = lazy(() => import(/* webpackChunkName: "maps" */ './pages/Canvassing'));

// Sales pages
const CanvassHome       = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/CanvassHome'));
const RepJobs           = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/RepJobs'));
const RepJobDetail      = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/RepJobDetail'));
const ManagerHome       = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/ManagerHome'));
const RepGuide          = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/RepGuide'));
const ManagerGuide      = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/ManagerGuide'));
const Team              = lazy(() => import(/* webpackChunkName: "sales" */ './pages/sales/Team'));

// Crew pages
const Today             = lazy(() => import(/* webpackChunkName: "field" */ './pages/Today'));
const PhoneSchedule     = lazy(() => import(/* webpackChunkName: "field" */ './pages/PhoneSchedule'));
const CrewJobs          = lazy(() => import(/* webpackChunkName: "crew" */ './pages/crew/CrewJobs'));
const CrewJobDetail     = lazy(() => import(/* webpackChunkName: "crew" */ './pages/crew/CrewJobDetail'));
const CrewInstructions  = lazy(() => import(/* webpackChunkName: "crew" */ './pages/crew/CrewInstructions'));

// Research bot page (single screen — see routeRoles.js's RESEARCH BOT segment)
const ResearchBot       = lazy(() => import(/* webpackChunkName: "research" */ './pages/ResearchBot'));

// Public token pages
const SigningPage        = lazy(() => import(/* webpackChunkName: "public" */ './pages/SigningPage'));
const PhotoReportPage    = lazy(() => import(/* webpackChunkName: "public" */ './pages/PhotoReportPage'));
// The homeowner's progress link. In the "public" chunk with the other
// no-login pages so it never pulls the owner bundle down a customer's phone.
const TrackerPage        = lazy(() => import(/* webpackChunkName: "public" */ './pages/TrackerPage'));
// The vendor/sub status buttons — same public chunk, same no-login token rule.
const VendorStatusPage   = lazy(() => import(/* webpackChunkName: "public" */ './pages/VendorStatusPage'));
const DailyLogReportPage = lazy(() => import(/* webpackChunkName: "public" */ './pages/DailyLogReportPage'));
const WoodReportPage     = lazy(() => import(/* webpackChunkName: "public" */ './pages/WoodReportPage'));
const FullReportPage     = lazy(() => import(/* webpackChunkName: "public" */ './pages/FullReportPage'));
const SupplementPackagePage = lazy(() => import(/* webpackChunkName: "public" */ './pages/SupplementPackagePage'));
const QuickRangePage     = lazy(() => import(/* webpackChunkName: "public" */ './pages/QuickRangePage'));
const StormReportPage    = lazy(() => import(/* webpackChunkName: "public" */ './pages/StormReportPage'));
const GuestPage          = lazy(() => import(/* webpackChunkName: "guest" */ './pages/guest/GuestPage'));

// Minimal centered fallback matching the app's existing loading style
function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', background: '#0f1123', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
      Loading...
    </div>
  );
}

// Grouped sidebar: Dashboard ungrouped, then four departments — Sales /
// Insurance / Production & Ops / Admin-Owner (per the UX/nav audit).
// Storm Tracker is deliberately listed in BOTH Sales and Insurance (same
// route, same component — it drives canvassing AND supports claims with
// storm/peril data); clicking either entry opens the same page. Items may
// carry a `featureFlag` key — see the Sidebar component below, which hides
// an item when settings/company.features[key] !== true (KLO-only
// lead-gen machinery, off by default for any other company).
const DEPARTMENTS = [
  { key: 'sales', label: 'Sales', items: [
    { path: '/leads',         label: 'Leads',          icon: '🎯' },
    { path: '/website-leads', label: 'Website Leads',  icon: '🌐' },
    { path: '/contacts',      label: 'Contacts',       icon: '👤' },
    { path: '/canvassing',    label: 'Canvassing',     icon: '🚪' },
    { path: '/prospects',     label: 'Prospects',      icon: '📋' },
    { path: '/proposals',     label: 'Proposals',      icon: '📄' },
    { path: '/map',           label: 'Map View',       icon: '🗺️' },
    { path: '/storm-tracker', label: 'Storm Tracker',  icon: '⚡' },
    { path: '/neighborhoods', label: 'Neighborhoods',  icon: '🏘' },
    { path: '/commercial',    label: 'Commercial',     icon: '🏢' },
    { path: '/contractors',   label: 'Contractors',    icon: '🏗' },
    { path: '/team',          label: 'Team',           icon: '👥' },
  ]},
  { key: 'insurance', label: 'Insurance', items: [
    { path: '/claims',        label: 'Claims',         icon: '🧾' },
    { path: '/storm-tracker', label: 'Storm Tracker',  icon: '⚡' },
  ]},
  { key: 'production-ops', label: 'Production & Ops', items: [
    { path: '/jobs',          label: 'Jobs',           icon: '🔨' },
    { path: '/permits',       label: 'Permits',        icon: '🏛' },
    { path: '/properties',    label: 'Properties',     icon: '🏠' },
    { path: '/takeoff',       label: 'Takeoff',        icon: '📐' },
    { path: '/metal-jobs',    label: 'Metal Jobs',     icon: '🪙' },
    { path: '/estimator',     label: 'Estimator',      icon: '💰' },
    { path: '/catalog',       label: 'Catalog',        icon: '📦' },
    { path: '/documents',     label: 'Documents',      icon: '📁' },
    { path: '/calendar',      label: 'Calendar',       icon: '📅' },
    { path: '/labor',         label: 'Crew Hours',     icon: '⏱' },
    { path: '/todos',         label: 'To-Do',          icon: '☑️' },
    { path: '/ai-agents',     label: 'AI Agents',      icon: '🤖' },
  ]},
  { key: 'admin-owner', label: 'Admin / Owner', items: [
    { path: '/os',            label: 'RoofBeam OS',    icon: '🤖' },
    { path: '/profit',        label: 'Profit Tracker', icon: '📈' },
    { path: '/approvals',     label: 'Approvals',      icon: '🔔', featureFlag: 'leadGen' },
    { path: '/messages',      label: 'Messages',       icon: '💬' },
    { path: '/settings',      label: 'Settings',       icon: '⚙️' },
  ]},
];

const SIDEBAR_OPEN_DEPTS_KEY = 'roofbeam_sidebar_open_depts';

function loadOpenDepts() {
  try {
    const raw = localStorage.getItem(SIDEBAR_OPEN_DEPTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore — fall through to all-open default */ }
  // Default: every department open on first visit.
  return DEPARTMENTS.reduce((acc, d) => { acc[d.key] = true; return acc; }, {});
}

function Sidebar() {
  const navigate = useNavigate();
  const [webNew, setWebNew] = useState(0);
  const unread = useTotalUnread();
  const pendingApprovals = usePendingApprovalsCount();
  const [openDepts, setOpenDepts] = useState(loadOpenDepts);
  // KLO-only lead-gen feature flag (settings/company.features.leadGen) — live,
  // so toggling it in Settings hides/shows Approvals without a re-login.
  // Defaults to true while the doc hasn't resolved yet (avoids a flash of a
  // missing nav item for the common case where it's on); the real access
  // control is the matching check in PrivateRoute.js, not this nav filter.
  const [leadGenOn, setLeadGenOn] = useState(true);

  useEffect(() => {
    return onSnapshot(doc(db, 'settings', 'company'),
      snap => setLeadGenOn(snap.exists() ? (snap.data().features || {}).leadGen === true : false),
      () => {});
  }, []);

  function toggleDept(key) {
    setOpenDepts(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(SIDEBAR_OPEN_DEPTS_KEY, JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  }

  // Count of new (uncontacted) website leads → sidebar badge.
  useEffect(() => {
    getCountFromServer(query(collection(db, 'websiteLeads'), where('status', '==', 'new')))
      .then(s => setWebNew(s.data().count)).catch(() => {});
  }, []);

  async function handleSignOut() {
    await signOut(auth);
    navigate('/login', { replace: true });
  }

  return (
    <nav className="sidebar">
      <div className="sidebar-brand">
        <RoofBeamWordmark size={34} />
      </div>
      <ul className="nav-list">
        <li>
          <NavLink to="/dashboard" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}>
            <span className="nav-icon">📊</span>
            <span className="nav-label">Dashboard</span>
          </NavLink>
        </li>
        {DEPARTMENTS.map(dept => {
          const items = dept.items.filter(it => !it.featureFlag || leadGenOn);
          if (!items.length) return null;
          const open = openDepts[dept.key] !== false;
          return (
            <React.Fragment key={dept.key}>
              <li
                className="nav-section"
                onClick={() => toggleDept(dept.key)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              >
                <span>{dept.label}</span>
                <span style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', display: 'inline-block', fontSize: 10 }}>▾</span>
              </li>
              {open && items.map(({ path, label, icon }) => (
                <li key={dept.key + path}>
                  <NavLink
                    to={path}
                    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                  >
                    <span className="nav-icon">{icon}</span>
                    <span className="nav-label">{label}</span>
                    {path === '/website-leads' && webNew > 0 && (
                      <span className="badge badge-red" style={{ marginLeft: 'auto' }}>{webNew}</span>
                    )}
                    {path === '/messages' && unread > 0 && (
                      <span className="badge badge-red" style={{ marginLeft: 'auto' }}>{unread}</span>
                    )}
                    {path === '/approvals' && pendingApprovals > 0 && (
                      <span className="badge badge-red" style={{ marginLeft: 'auto' }}>{pendingApprovals}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </React.Fragment>
          );
        })}
      </ul>
      <div className="sidebar-footer">
        <button onClick={handleSignOut} className="signout-btn">
          <span className="nav-icon">🚪</span>
          <span className="nav-label">Sign Out</span>
        </button>
        <BuildStamp style={{ padding: '6px 0 2px', textAlign: 'center' }} />
      </div>
    </nav>
  );
}

// Same Sell → Build → Run order as the sidebar.
// ⚠ THE FIRST THREE WERE BOTTOM TABS UNTIL FIELD MODE. Dashboard, Leads and
// Storm are READING screens — counts, charts, a map — and the owner's decision
// was that a phone in a driveway is for DOING, so the five slots went to
// Today · Jobs · ➕ · Schedule · More and these moved in here. They are first
// in the list because that is where somebody will look for them.
const DRAWER_NAV = [
  { path: '/dashboard',     label: 'Dashboard',      icon: '📊' },
  { path: '/leads',         label: 'Leads',          icon: '🎯' },
  { path: '/storm-tracker', label: 'Storm Tracker',  icon: '⚡' },
  { path: '/approvals',     label: 'Approvals',      icon: '🔔' },
  { path: '/messages',      label: 'Messages',       icon: '💬' },
  { path: '/contacts',      label: 'Contacts',       icon: '👤' },
  { path: '/canvassing',    label: 'Canvassing',     icon: '🚪' },
  { path: '/prospects',     label: 'Prospects',      icon: '📋' },
  { path: '/proposals',     label: 'Proposals',      icon: '📄' },
  { path: '/map',           label: 'Map View',       icon: '🗺️' },
  { path: '/neighborhoods', label: 'Neighborhoods',  icon: '🏘' },
  { path: '/properties',    label: 'Properties',     icon: '🏠' },
  { path: '/takeoff',       label: 'Takeoff',        icon: '📐' },
  { path: '/metal-jobs',    label: 'Metal Jobs',     icon: '🪙' },
  { path: '/estimator',     label: 'Estimator',      icon: '💰' },
  { path: '/catalog',       label: 'Catalog',        icon: '📦' },
  { path: '/documents',     label: 'Documents',      icon: '📁' },
  { path: '/commercial',    label: 'Commercial',     icon: '🏢' },
  { path: '/contractors',   label: 'Contractors',    icon: '🏗' },
  { path: '/calendar',      label: 'Calendar',       icon: '📅' },
  { path: '/labor',         label: 'Crew Hours',     icon: '⏱' },
  { path: '/todos',         label: 'To-Do',          icon: '☑️' },
  { path: '/profit',        label: 'Profit Tracker', icon: '📈' },
  { path: '/ai-agents',     label: 'AI Agents',      icon: '🤖' },
  { path: '/os',            label: 'RoofBeam OS',    icon: '🤖' },
  { path: '/team',          label: 'Team',           icon: '👥' },
  { path: '/settings',      label: 'Settings',       icon: '⚙️' },
];

function GlobalJobSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null); // null = not searched; else {jobs,leads,prospects,contacts}
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      httpsCallable(functions, 'globalSearch')({ term })
        .then(res => setResults(res.data || { jobs: [], leads: [], prospects: [], contacts: [] }))
        .catch(() => setResults({ jobs: [], leads: [], prospects: [], contacts: [] }))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  function go(path) {
    setQ('');
    setOpen(false);
    navigate(path);
  }

  const groups = results ? [
    { key: 'jobs',      label: 'Jobs',      items: results.jobs,      row: j => ({ text: `${j.address || '—'} · ${j.customerName || '—'}`, path: `/jobs/${j.id}` }) },
    { key: 'leads',     label: 'Leads',     items: results.leads,     row: l => ({ text: `${l.address || '—'} · ${l.ownerName || l.owner || '—'}`, path: `/leads?leadId=${l.id}` }) },
    { key: 'prospects', label: 'Prospects', items: results.prospects, row: p => ({ text: `${p.address || '—'} · ${p.owner || '—'}`, path: `/prospects?open=${p.id}` }) },
    { key: 'contacts',  label: 'Contacts',  items: results.contacts,  row: c => ({ text: `${c.name || '—'} · ${c.phone || c.email || '—'}`, path: `/contacts/${c.id}` }) },
  ].filter(g => g.items && g.items.length) : [];
  const totalMatches = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 460 }}>
      <input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search jobs, leads, prospects, contacts…"
        style={{ width: '100%', padding: '7px 12px', fontSize: 13, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, color: '#fff', boxSizing: 'border-box', outline: 'none' }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 3000, background: '#1a1d2e', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.45)', maxHeight: 420, overflowY: 'auto' }}>
          {searching ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Searching…</div>
          ) : totalMatches === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>No matches</div>
          ) : groups.map(g => (
            <div key={g.key}>
              <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{g.label}</div>
              {g.items.map(item => {
                const { text, path } = g.row(item);
                return (
                  <div key={item.id} onMouseDown={() => go(path)}
                    style={{ padding: '9px 12px', fontSize: 12.5, color: '#eee', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    {text}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ⚠ A HEAVY OFFICE SCREEN, IN FIELD MODE ──────────────────────────────────
// A read-only summary and the device to open the real thing on. The reason is
// always concrete (see HEAVY_ROUTES) because "optimised for desktop" reads as
// an excuse. The escape hatch is explicit and is not the default.
function FieldGuard({ children }) {
  const location = useLocation();
  const [anyway, setAnyway] = useState('');
  const heavy = heavyRoute(location.pathname);
  // ⚠ The override is per PATH, so stepping into a job and back out does not
  // silently leave every heavy screen unlocked for the session.
  if (!heavy || anyway === location.pathname) return children;
  return (
    <BestOnLargerScreen what={heavy.what} why={heavy.why} onOpenAnyway={() => setAnyway(location.pathname)}>
      {/* ⚠ THE REAL SCREEN, UNDERNEATH AND NOT INTERACTIVE. Everything worth
          reading is here — only the editing is withheld. `inert` is ignored by
          older WebViews, so pointer-events carries it as well. */}
      <div inert="" aria-hidden="false" style={{ pointerEvents: 'none', opacity: 0.82 }}>
        {children}
      </div>
    </BestOnLargerScreen>
  );
}

function AppLayout() {
  const { mode } = useLayoutMode();
  const unread = useTotalUnread();

  if (mode === FIELD) {
    return (
      <div className="app-shell-mobile">
        <div className="mobile-topbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <RoofBeamWordmark size={26} />
          <BuildStamp style={{ marginLeft: 'auto', paddingRight: 4 }} />
        </div>
        <main className="main-content-mobile">
          <div style={{ position: 'sticky', top: 0, zIndex: 1500, display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#151728' }}>
            <GlobalJobSearch />
          </div>
          <FieldGuard><Outlet /></FieldGuard>
        </main>
        {/* ⚠ Today · Jobs · ➕ · Schedule · More. The old four-tab bar
            (Dashboard/Leads/Jobs/Storm) put three reading screens in front of
            somebody standing in a driveway; those are in More now. */}
        <FieldNav more={DRAWER_NAV} unread={unread} />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', padding: '8px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#151728' }}>
            <GlobalJobSearch />
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

function CrewLayout() {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#0f1123', fontFamily: 'Arial, sans-serif' }}>
      <Outlet />
      {/* Sign-out accessible via each page's own nav — no shared header needed here */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1d2e', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px 20px', textAlign: 'right' }}>
        <button
          onClick={async () => { await signOut(auth); navigate('/login', { replace: true }); }}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 12 }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// Root gate: logged-out visitors get the marketing landing; authenticated
// users are sent exactly where the app sends them after sign-in (the same
// roleDestination Login.js uses — crew/rep/manager portals, owner dashboard).
function RootGate() {
  const [dest, setDest] = useState('checking'); // 'checking' | 'out' | route string
  // BUG FIX (navigation/live-updates follow-up, ask #3): same unbounded wait
  // as PrivateRoute's own user===undefined case — nothing here ever timed
  // out, so a stuck Auth/Firestore connection (a second browser window hit
  // exactly this before the persistence fix in firebase/config.js) left this
  // blank indefinitely instead of ever reaching Landing or a real route.
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (!user) { setDest('out'); return; }
      try {
        const tr = await user.getIdTokenResult();
        setDest(roleDestination(tr.claims));
      } catch {
        // BUG FIX (follow-up #2 — same root cause as PrivateRoute.js's
        // claimsError): this used to hardcode '/dashboard' on ANY
        // getIdTokenResult failure, which routeRoles.js's Phase C cutover
        // denies to a null/unresolved role same as every other route —
        // PrivateRoute would redirect right back to roleDestination's own
        // result, an infinite self-redirect that never renders anything.
        // One retry with a forced token refresh first; only show the Retry
        // screen (below) if it genuinely keeps failing.
        try {
          const tr = await user.getIdTokenResult(true);
          setDest(roleDestination(tr.claims));
        } catch {
          setDest('error');
        }
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (dest !== 'checking') return undefined;
    const timer = setTimeout(() => setTimedOut(true), 12000);
    return () => clearTimeout(timer);
  }, [dest]);

  if ((dest === 'checking' && timedOut) || dest === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1123', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
            {dest === 'error' ? "Couldn't verify your account. Check your connection and retry." : 'Taking longer than expected to load. Check your connection and retry.'}
          </div>
          <button onClick={() => window.location.reload()}
            style={{ padding: '10px 20px', background: '#e91e8c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  // Avoid a FLASH of a loading spinner for the overwhelmingly common case
  // (auth resolves in well under a second) — only show one once it's taken
  // long enough to actually be noticeable, same threshold PageLoader (the
  // Suspense fallback right below this component) already uses implicitly
  // by existing at all.
  if (dest === 'checking') return null;
  if (dest === 'out') return <Landing />;
  return <Navigate to={dest} replace />;
}

// Native push lives here rather than in App() because the tap handler needs
// useNavigate(), which only works inside the Router. Renders nothing, asks
// for nothing — see src/utils/pushNotifications.js.
function PushBridge() {
  usePushNotifications();
  return null;
}

function App() {
  useAppResumeAuthCheck();
  useNativeBackButton();
  return (
    <BrowserRouter>
      {/* Mounted once, present on every route (including public token pages —
          SigningPage.js uses it too) so appAlert()/appConfirm() work from
          anywhere without each page remounting its own host. */}
      <AppAlertHost />
      {/* Same "everywhere, including public pages" reasoning as AppAlertHost
          above — a stale bundle is just as real a problem for an anonymous
          signing/report page as it is for the app shell. */}
      <UpdateBanner />
      {/* Native push: listeners + notification-tap deep-linking. No-op on
          web and on a device that hasn't granted permission. */}
      <PushBridge />
      {/* ⚠ "Add RoofBeam to your Home Screen", once, after login, on a phone
          in an ordinary browser tab. It renders nothing for anyone else —
          anonymous visitors on public signing/report links, desktops, the
          Android app, and anyone already launching from the Home Screen icon.
          Mounted here rather than per-page because it has to reach the crew
          app and the rep portal too, and those have no shared shell. On iOS
          this is the ONLY route to notifications ever working. */}
      <AddToHomeScreenPrompt />
      {/* One Suspense over the whole table — no auth dependency, so public
          token routes (/canvass/:token, /sign/:token) load lazily for
          anonymous visitors exactly like everything else */}
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* PUBLIC — no layout, no auth */}
        <Route path="/"                    element={<RootGate />} />
        <Route path="/sign/:token"          element={<SigningPage />} />
        <Route path="/canvass/:token"       element={<CanvassPage />} />
        <Route path="/photo-report/:token" element={<PhotoReportPage />} />
        <Route path="/track/:token"        element={<TrackerPage />} />
        <Route path="/v/:token"            element={<VendorStatusPage />} />
        <Route path="/daily-report/:token" element={<DailyLogReportPage />} />
        <Route path="/wood-report/:token"  element={<WoodReportPage />} />
        <Route path="/report/:token"       element={<FullReportPage />} />
        <Route path="/supplement/:token"   element={<SupplementPackagePage />} />
        <Route path="/range/:token"        element={<QuickRangePage />} />
        <Route path="/storm-report/:token" element={<StormReportPage />} />
        <Route path="/g"                   element={<GuestPage />} />
        <Route path="/login"               element={<Login />} />

        {/* SALES — auth required, no sidebar */}
        <Route element={<PrivateRoute />}>
          <Route path="/canvass-home"     element={<CanvassHome />} />
          <Route path="/rep/jobs"         element={<RepJobs />} />
          <Route path="/rep/jobs/:jobId"  element={<RepJobDetail />} />
          <Route path="/rep/guide"        element={<RepGuide />} />
          <Route path="/rep/messages"           element={<Messages />} />
          <Route path="/rep/messages/:threadId" element={<Messages />} />
          <Route path="/manager"          element={<ManagerHome />} />
          <Route path="/manager/guide"    element={<ManagerGuide />} />
          {/* ⚠️ /team MOVED OUT OF THIS GROUP. The owner's sidebar links to
              /team, but the only route for it lived here — in the sales shell,
              which has no sidebar — so the owner's Team page opened as a
              standalone pink-header screen. The owner's /team is now in the
              AppLayout group below; a sales MANAGER, who lives in this shell
              and has no sidebar, keeps it here under /manager/team. One
              component, two shells, chosen by the `chrome` prop. */}
          <Route path="/manager/team"     element={<Team chrome="standalone" />} />
        </Route>

        {/* CREW — auth required, no sidebar */}
        <Route element={<PrivateRoute />}>
          <Route element={<CrewLayout />}>
            <Route path="/crew"                    element={<CrewJobs />} />
            <Route path="/crew/jobs/:jobId"        element={<CrewJobDetail />} />
            <Route path="/crew/instructions"       element={<CrewInstructions />} />
            <Route path="/crew/messages"           element={<Messages />} />
            <Route path="/crew/messages/:threadId" element={<Messages />} />
          </Route>
        </Route>

        {/* RESEARCH BOT — auth required, no sidebar, one screen */}
        <Route element={<PrivateRoute />}>
          <Route path="/research" element={<ResearchBot />} />
        </Route>

        {/* PRIVATE — auth required + sidebar layout */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppLayout />}>
            {/* ── FIELD MODE's two screens ──────────────────────────────
                ⚠ ORDINARY ROUTES, open on every device. The MODE decides the
                chrome, never where you are — so /today is a perfectly good
                morning screen on a laptop and a link to it works anywhere.
                Same roles as /dashboard: everyone who can see jobs. */}
            <Route path="/today"                         element={<Today />} />
            <Route path="/schedule"                      element={<PhoneSchedule />} />
            <Route path="/dashboard"                     element={<Dashboard />} />
            <Route path="/leads"                         element={<Leads />} />
            <Route path="/website-leads"                 element={<WebsiteLeads />} />
            <Route path="/canvassing"                    element={<Canvassing />} />
            <Route path="/prospects"                     element={<Prospects />} />
            <Route path="/proposals"                     element={<Proposals />} />
            <Route path="/jobs"                          element={<Jobs />} />
            <Route path="/jobs/:id"                      element={<JobDetail />} />
            {/* BUG FIX (navigation follow-up, ask #1): the job's active tab
                lives in the URL (see JobDetail.js's tabSlug/goToTab) so a
                tab switch is a real back-able history entry. */}
            <Route path="/jobs/:id/:tabSlug"             element={<JobDetail />} />
            <Route path="/storm-tracker"                 element={<StormTracker />} />
            <Route path="/neighborhoods"                 element={<NeighborhoodIntel />} />
            <Route path="/map"                           element={<MapView />} />
            <Route path="/contacts"                      element={<ContactsSplit />} />
            <Route path="/contacts/:id"                  element={<ContactsSplit />} />
            <Route path="/properties"                    element={<Properties />} />
            <Route path="/properties/:id"                element={<PropertyDetail />} />
            <Route path="/takeoff"                       element={<Takeoff />} />
            <Route path="/metal-jobs"                    element={<MetalJobs />} />
            <Route path="/estimator"                     element={<Estimator />} />
            <Route path="/catalog"                       element={<CatalogPage />} />
            <Route path="/documents"                     element={<Documents />} />
            <Route path="/documents/view/:jobId/:docId"  element={<DocumentViewer />} />
            <Route path="/calendar"                      element={<Calendar />} />
            {/* Who worked where, how long — the day/week half of the crew
                labor log. The job's own Field tab has the per-job half. */}
            <Route path="/labor"                         element={<LaborLog />} />
            <Route path="/todos"                         element={<Todos />} />
            <Route path="/messages"                      element={<Messages />} />
            <Route path="/messages/:threadId"            element={<Messages />} />
            <Route path="/profit"                        element={<ProfitTracker />} />
            <Route path="/contractors"                   element={<Contractors />} />
            <Route path="/commercial"                    element={<Commercial />} />
            <Route path="/ai-agents"                     element={<AIAgents />} />
            <Route path="/settings"                      element={<Settings />} />
            <Route path="/solar-batch"                   element={<SolarBatch />} />
            <Route path="/approvals"                     element={<Approvals />} />
            <Route path="/claims"                        element={<Claims />} />
            <Route path="/permits"                       element={<Permits />} />
            <Route path="/team"                          element={<Team chrome="app" />} />
            <Route path="/os"                            element={<RoofbeamOS />} />
            {/* 404 — LAST, and inside the layout on purpose: an unknown URL
                used to match nothing at all, so <Routes> rendered null and the
                app went black with no menu and no way back. A non-owner role
                never reaches this (PrivateRoute's route guard denies an
                unlisted path and sends them to their own home, which is the
                right answer for them); the owner gets a named page with the
                sidebar still in place. */}
            <Route path="*"                              element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
